import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, dt } from "@/lib/api";
import { ROLE_DESC } from "@/lib/roles";
import { Clock, CheckCircle2, XCircle, RefreshCw, KeyRound } from "lucide-react";

const inputCls = "w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none rounded-md px-3 py-2.5";
const REQ_ROLES = ["MEKANIK", "KASIR", "GUDANG", "ADMIN", "MANAGEMENT"];

function RequestStatus({ req }) {
  if (!req) return null;
  const map = {
    REQUESTED: [Clock, "text-amber-300 bg-amber-950/50 border-amber-900", "Permintaan terkirim — menunggu persetujuan Owner"],
    REJECTED: [XCircle, "text-red-300 bg-red-950/60 border-red-900", `Ditolak${req.reason ? `: ${req.reason}` : ""}`],
    APPROVED: [CheckCircle2, "text-emerald-300 bg-emerald-950/50 border-emerald-900", "Disetujui"],
  };
  const [Icon, cls, text] = map[req.status] || map.REQUESTED;
  return (
    <div className={`border rounded p-3 text-sm flex items-start gap-2 ${cls}`} data-testid="access-request-status">
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div>{text}<div className="text-[11px] opacity-70 mt-0.5">Peran diminta: <span className="font-mono">{req.requested_role}</span> · {dt(req.at)}</div></div>
    </div>
  );
}

export default function Pending() {
  const { user, logout, setUser, refresh } = useAuth();
  const req = user.access_request;
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", requested_role: req?.requested_role || "MEKANIK", note: req?.note || "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!req || req.status === "REJECTED");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/access-request", form); setUser(data); setEditing(false); toast.success("Permintaan akses terkirim"); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const claim = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/claim-invite", { code }); setUser(data); toast.success(`Akun aktif sebagai ${data.role}`); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen mjm-bg mjm-grain flex items-center justify-center p-6">
      <div className="w-full max-w-lg panel-red p-8 fade-up" data-testid="pending-page">
        <img src="/logo.png" alt="MJM" className="h-12 mx-auto mb-5" />
        <div className="eyebrow">Akun belum aktif</div>
        <h1 className="font-display text-3xl font-bold mt-1">Lengkapi data diri</h1>
        <p className="text-zinc-400 text-sm mt-1 mb-5">Masuk sebagai <span className="text-white font-mono">{user.email}</span>. Owner akan meninjau dan memberi peran.</p>

        <RequestStatus req={req} />

        {editing ? (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="access-request-form">
            <div><label className="text-xs text-zinc-400 uppercase tracking-wider">Nama lengkap</label><input data-testid="pending-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} mt-1`} required /></div>
            <div><label className="text-xs text-zinc-400 uppercase tracking-wider">No. HP / WhatsApp</label><input data-testid="pending-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" className={`${inputCls} font-mono mt-1`} required /></div>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider">Peran yang diminta</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {REQ_ROLES.map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, requested_role: r })} data-testid={`pending-role-${r}`}
                    className={`text-left p-2 rounded border text-xs transition-colors ${form.requested_role === r ? "border-red-600 bg-red-950/50 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                    <div className="font-mono font-bold">{r}</div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">{ROLE_DESC[form.requested_role]}</p>
            </div>
            <div><label className="text-xs text-zinc-400 uppercase tracking-wider">Catatan (opsional)</label><textarea data-testid="pending-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="mis. mulai kerja Senin, referensi Pak Andi" className={`${inputCls} mt-1 text-sm`} /></div>
            <button type="submit" disabled={busy} data-testid="pending-submit" className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-md py-2.5 transition-colors">{busy ? "Mengirim…" : req ? "Kirim ulang permintaan" : "Kirim permintaan akses"}</button>
          </form>
        ) : (
          <div className="flex gap-2 mt-4">
            <button onClick={refresh} data-testid="pending-refresh-btn" className="flex-1 flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 rounded-md py-2.5 text-sm"><RefreshCw className="h-4 w-4" /> Cek status</button>
            <button onClick={() => setEditing(true)} data-testid="pending-edit-btn" className="flex-1 border border-zinc-700 hover:border-zinc-500 rounded-md py-2.5 text-sm">Ubah data</button>
          </div>
        )}

        <form onSubmit={claim} className="mt-6 pt-5 border-t border-zinc-800/80" data-testid="claim-invite-form">
          <div className="text-xs text-zinc-400 uppercase tracking-wider flex items-center gap-1"><KeyRound className="h-3 w-3" /> Punya kode aktivasi dari Owner?</div>
          <div className="flex gap-2 mt-2">
            <input data-testid="claim-code-input" value={code} maxLength={6} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="XXXXXX" className={`${inputCls} font-mono tracking-[0.4em] text-center`} />
            <button type="submit" disabled={busy || code.length !== 6} data-testid="claim-code-submit" className="px-4 bg-zinc-100 hover:bg-white disabled:opacity-50 text-zinc-900 font-semibold rounded-md text-sm">Aktifkan</button>
          </div>
        </form>

        <button onClick={logout} data-testid="pending-logout-btn" className="mt-6 w-full text-sm text-red-400 hover:text-red-300">Keluar</button>
      </div>
    </div>
  );
}
